<template>
<div id="sheet-preview">
  <div v-if="state.tabbed">
    <tabs pills v-model="selectedTab" :transition-duration="0" justified>
      <tab v-for="(t, k) in tabs"
           :title="t.title"
           :key="t.id">
        <ul>
          <li v-for="(l, i) in t.lines"
              :key="l.id" >
            <div v-if="l.show"
                 :class="{ 'empty': l.empty && (i + t.start !== selection)}">
              <component :is="l.type"
                         :selected="k === selectedTab && (i + t.start === selection)"
                         :value="l.value"
                         :note="l.note"
                         :parameters="l.parameters"
                         @commit="commit($event)"
                         @select="select(i + t.start)">
              </component>
            </div>
          </li>
        </ul>
      </tab>
    </tabs>
  </div>
  <div v-else>
    <ul>
      <li v-for="(l, i) in lines"
          :key="l.id" >
        <div v-if="l.show"
             :class="{ 'empty': l.empty && (i !== selection)}">
          <component :is="l.type"
                     :selected="i === selection"
                     :value="l.value"
                     :note="l.note"
                     :parameters="l.parameters"
                     @commit="commit($event)"
                     @select="select(i)">
          </component>
        </div>
      </li>
    </ul>
  </div>
</div>
</template>
<script>
import assert from '@/sui/tools/assert'

import fieldPreview from '@/sui/components/sheet/add/fieldPreview.vue'
import titlePreview from '@/sui/components/sheet/add/titlePreview.vue'
import arrayPreview from '@/sui/components/sheet/add/arrayPreview.vue'

import impl from '@/sui/components/sheet/add/impl/preview.js'

export default {
  name: 'sheet-add-preview',
  props: {
    sheet: {
      type: Object,
      required: true
    },

    notes: {
      type: Object,
      required: true
    },

    cursor: {
      type: Object,
      required: true
    },

    steps: {
      type: Object,
      required: true
    },

    config: {
      type: Object,
      required: true
    },

    state: {
      type: Object,
      required: true
    }
  },

  data: function () {
    return {
      disableSelection: true,
      selectedTab: 0
    }
  },

  computed: {
    type: function () {
      return this.sheet.type
    },

    selection: function () {
      if (this.cursor == null) {
        return null
      }

      for (let i in this.lines) {
        const l = this.lines[i]
        if (l.type === 'field-preview' &&
            l.show &&
            l.parameters.config.field === this.cursor.field &&
            l.target === this.cursor.target) {
          return parseInt(i, 10)
        }
      }

      return null
    },

    tabs: function () {
      if (!this.state.tabbed) {
        return []
      }

      const tabs = []
      let current = null
      this.lines.forEach(
        l => {
          if (l.type === 'tab') {
            if (current != null) {
              tabs.push(current)
            }

            current = {
              lines: [],
              title: l.parameters.text,
              id: l.id,
              start: current == null ? 1 : current.start + current.lines.length + 1
            }
          } else {
            assert.notNull(current)
            current.lines.push(l)
          }
        }
      )
      if (current != null) {
        tabs.push(current)
      }
      return tabs
    },

    lines: function () {
      if (this.steps == null) {
        return []
      }

      const { titles, repeatableTitles, fields } = this.steps
      if (titles == null || fields == null ||
          titles.length === 0 || fields.length === 0) {
        return []
      }

      let result = []
      let j = 0
      let k = 0
      let arrayCount = 0
      const count = titles.length
      for (let i in fields) {
        const field = fields[i]
        while (j < count && field.field === titles[j].start) {
          const t = titles[j]
          if (t.type === 'tab') {
            if (this.state.tabbed) {
              result.push(impl.newTab(i, t))
            }
          } else {
            const fieldIgnore = t.hiddenWhenFieldIgnored &&
                                field.ignore != null &&
                                field.ignore(this.sheet, this.sheet)
            const titleIgnore = t.ignore != null &&
                                t.ignore(this.sheet, this.sheet)
            if (!fieldIgnore && !titleIgnore) {
              result.push(impl.newTitle(j, t))
            }
          }
          ++j
        }
        k = this.checkRepeatableTitles(
          repeatableTitles,
          field,
          this.sheet,
          k,
          result,
          this.sheet
        )

        if (field.type !== 'array') {
          result.push(
            impl.newField(
              this,
              i,
              field,
              this.sheet,
              this.notes[field.field],
              this.sheet
            )
          )
        } else {
          let current = [
            {
              field: field,
              target: this.sheet,
              notes: this.notes,
              index: 0,
              item: 0
            }
          ]

          while (current.length > 0) {
            let it = current[current.length - 1]
            const items = it.target[it.field.field]
            const notes = it.notes[it.field.field]

            const item = items[it.item]
            const note = notes[it.item]
            if (it.index === 0) {
              const newArray = impl.newArray(
                current.length - 1,
                arrayCount++,
                it.item,
                it.field,
                item,
                it.target,
                this.sheet
              )
              result.push(newArray)
              it.show = newArray.show
            }

            const subfield = it.field.config.fields[it.index]
            k = this.checkRepeatableTitles(
              repeatableTitles,
              subfield,
              item,
              k,
              result,
              this.sheet
            )
            if (subfield.type !== 'array') {
              result.push(
                impl.newSubfield(
                  this,
                  current.length,
                  it.field.field,
                  item,
                  it.index,
                  subfield,
                  it.show,
                  note[subfield.field],
                  this.sheet
                )
              )
              it.index++
              while (it.index === it.field.config.fields.length) {
                it.index = 0
                it.item++
                if (it.item === it.target[it.field.field].length) {
                  current = current.slice(0, -1)
                  if (current.length === 0) {
                    break
                  }
                  it = current[current.length - 1]
                  it.index++
                }
              }
            } else {
              current.push({
                field: subfield,
                target: item,
                notes: note,
                index: 0,
                item: 0
              })
            }
          }
        }
      }
      return result
    }
  },

  methods: {
    commit: function (e) {
      this.$emit('commit', e)
    },

    select: function (i) {
      assert(i >= 0 && i < this.lines.length)

      const line = this.lines[i]
      if (line.parameters.config.readOnly) {
        return
      }

      const target = {
        next: i > this.selection,
        target: line.target,
        field: line.parameters.config.field
      }
      this.$emit('select', target)
    },

    checkRepeatableTitles: function (titles, field, value, k, result, sheet) {
      if (titles == null) {
        return k
      }

      let index = k
      for (let i in titles) {
        const title = titles[i]
        if (field.field === title.start) {
          result.push(
            impl.newRepeatableTitle(index, title, field, value, sheet)
          )
          ++index
        }
      }
      return result
    }
  },

  watch: {
    selection: function (value) {
      if (!this.state.tabbed) {
        return
      }

      for (let i = this.tabs.length - 1; i >= 0; --i) {
        if (this.tabs[i].start <= value) {
          this.selectedTab = i
          return
        }
      }
    }
  },

  components: {
    'title-preview': titlePreview,
    'field-preview': fieldPreview,
    'array-preview': arrayPreview
  }
}
</script>
<style scoped lang="less">
#sheet-preview {
    padding-bottom: 2rem;

    ul {
        padding-left: 0rem;
        list-style-type: none;
    }

    h1 {
        margin-bottom: 3rem;
    }

    .empty {
        opacity: 0.3;
    }
}

</style>
